import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bell, Check, Clock, UserPlus, Target, Briefcase, FileCheck, Users,
  MessageSquare, AlertTriangle, Info, Sparkles, Trash2, CheckCircle,
  ExternalLink, MoreHorizontal
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';
import type { Notification } from '../../lib/types';
import { NotificationDetails } from './NotificationDetails';

// Mappa tipo notifica -> icona e colore
const notificationTypeConfig: Record<string, { icon: React.ElementType; color: string; bgColor: string }> = {
  // Tasks
  task_assigned: { icon: UserPlus, color: 'text-blue-600 dark:text-blue-400', bgColor: 'bg-blue-100 dark:bg-blue-900/50' },
  sales_activity_assigned: { icon: UserPlus, color: 'text-blue-600 dark:text-blue-400', bgColor: 'bg-blue-100 dark:bg-blue-900/50' },
  // Segnalazione/funzionalita' richiesta dall'utente risolta (trigger trg_user_feedback_on_resolve)
  feedback_resolved: { icon: Check, color: 'text-green-600 dark:text-green-400', bgColor: 'bg-green-100 dark:bg-green-900/50' },
  task_completed: { icon: Check, color: 'text-green-600 dark:text-green-400', bgColor: 'bg-green-100 dark:bg-green-900/50' },
  task_due_soon: { icon: Clock, color: 'text-orange-600 dark:text-orange-400', bgColor: 'bg-orange-100 dark:bg-orange-900/50' },
  task_overdue: { icon: AlertTriangle, color: 'text-red-600 dark:text-red-400', bgColor: 'bg-red-100 dark:bg-red-900/50' },
  task_mention: { icon: MessageSquare, color: 'text-purple-600 dark:text-purple-400', bgColor: 'bg-purple-100 dark:bg-purple-900/50' },
  mention: { icon: MessageSquare, color: 'text-purple-600 dark:text-purple-400', bgColor: 'bg-purple-100 dark:bg-purple-900/50' },

  // Leads
  lead_new: { icon: UserPlus, color: 'text-emerald-600 dark:text-emerald-400', bgColor: 'bg-emerald-100 dark:bg-emerald-900/50' },
  lead_assigned: { icon: Users, color: 'text-emerald-600 dark:text-emerald-400', bgColor: 'bg-emerald-100 dark:bg-emerald-900/50' },
  lead_stage_changed: { icon: Target, color: 'text-emerald-600 dark:text-emerald-400', bgColor: 'bg-emerald-100 dark:bg-emerald-900/50' },
  lead_converted: { icon: Check, color: 'text-emerald-600 dark:text-emerald-400', bgColor: 'bg-emerald-100 dark:bg-emerald-900/50' },

  // Deals
  deal_new: { icon: Briefcase, color: 'text-indigo-600 dark:text-indigo-400', bgColor: 'bg-indigo-100 dark:bg-indigo-900/50' },
  deal_assigned: { icon: Users, color: 'text-indigo-600 dark:text-indigo-400', bgColor: 'bg-indigo-100 dark:bg-indigo-900/50' },
  deal_status_changed: { icon: Target, color: 'text-indigo-600 dark:text-indigo-400', bgColor: 'bg-indigo-100 dark:bg-indigo-900/50' },
  deal_won: { icon: FileCheck, color: 'text-green-600 dark:text-green-400', bgColor: 'bg-green-100 dark:bg-green-900/50' },
  deal_lost: { icon: AlertTriangle, color: 'text-red-600 dark:text-red-400', bgColor: 'bg-red-100 dark:bg-red-900/50' },
  deal_suggestion_from_whatsapp: { icon: Sparkles, color: 'text-purple-600 dark:text-purple-400', bgColor: 'bg-purple-100 dark:bg-purple-900/50' },

  // Clients
  client_new: { icon: UserPlus, color: 'text-cyan-600 dark:text-cyan-400', bgColor: 'bg-cyan-100 dark:bg-cyan-900/50' },
  client_updated: { icon: Users, color: 'text-cyan-600 dark:text-cyan-400', bgColor: 'bg-cyan-100 dark:bg-cyan-900/50' },
  // Documenti richiesti: arrivano dal cliente, quindi stessa famiglia
  documents_delivered: { icon: FileCheck, color: 'text-cyan-600 dark:text-cyan-400', bgColor: 'bg-cyan-100 dark:bg-cyan-900/50' },
  documents_completed: { icon: FileCheck, color: 'text-green-600 dark:text-green-400', bgColor: 'bg-green-100 dark:bg-green-900/50' },

  // WhatsApp
  whatsapp: { icon: MessageSquare, color: 'text-green-600 dark:text-green-400', bgColor: 'bg-green-100 dark:bg-green-900/50' },

  // Default per type generico
  info: { icon: Info, color: 'text-blue-600 dark:text-blue-400', bgColor: 'bg-blue-100 dark:bg-blue-900/50' },
  warning: { icon: AlertTriangle, color: 'text-orange-600 dark:text-orange-400', bgColor: 'bg-orange-100 dark:bg-orange-900/50' },
  error: { icon: AlertTriangle, color: 'text-red-600 dark:text-red-400', bgColor: 'bg-red-100 dark:bg-red-900/50' },
  success: { icon: Check, color: 'text-green-600 dark:text-green-400', bgColor: 'bg-green-100 dark:bg-green-900/50' },
};

// Mappa categoria da event_type
const getCategoryFromEventType = (eventType?: string): string => {
  if (!eventType) return 'altro';
  if (eventType.startsWith('task_') || eventType.startsWith('sales_activity_') || eventType === 'mention') return 'task';
  if (eventType.startsWith('lead_')) return 'lead';
  if (eventType.startsWith('deal_')) return 'deal';
  if (eventType.startsWith('client_') || eventType.startsWith('documents_')) return 'client';
  if (eventType.startsWith('project_')) return 'project';
  if (eventType.startsWith('whatsapp_') || eventType === 'whatsapp') return 'whatsapp';
  return 'altro';
};

// Colori badge categoria
const categoryColors: Record<string, string> = {
  task: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300',
  lead: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300',
  deal: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300',
  client: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/50 dark:text-cyan-300',
  project: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300',
  whatsapp: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300',
  altro: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
};

// Labels categoria in italiano
const categoryLabels: Record<string, string> = {
  task: 'Task',
  lead: 'Lead',
  deal: 'Deal',
  client: 'Cliente',
  project: 'Progetto',
  whatsapp: 'WhatsApp',
  altro: 'Altro',
};

interface NotificationItemProps {
  notification: Notification & {
    event_type?: string;
    sender_name?: string;
    isWhatsApp?: boolean;
  };
  onMarkAsRead: (id: string) => void;
  onDelete: (id: string) => void;
  onClick?: (notification: Notification) => void;
  isSelected?: boolean;
  onToggleSelect?: (id: string) => void;
  showCheckbox?: boolean;
  compact?: boolean;
}

export function NotificationItem({
  notification,
  onMarkAsRead,
  onDelete,
  onClick,
  isSelected = false,
  onToggleSelect,
  showCheckbox = false,
  compact = false,
}: NotificationItemProps) {
  const [isHovered, setIsHovered] = useState(false);

  // Determina icona e colori
  const isWhatsApp = notification.isWhatsApp || !!notification.whatsapp_chat_id;
  const eventType = isWhatsApp ? 'whatsapp' : notification.event_type;
  const config = notificationTypeConfig[eventType || ''] ||
                 notificationTypeConfig[notification.type] ||
                 notificationTypeConfig.info;
  const IconComponent = config.icon;

  // Categoria per badge
  const category = getCategoryFromEventType(eventType);
  const categoryColor = categoryColors[category];
  const categoryLabel = categoryLabels[category];

  // Formatta tempo
  const timeAgo = formatDistanceToNow(new Date(notification.created_at), {
    addSuffix: true,
    locale: it
  });

  const handleClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.quick-action')) {
      return; // Non propagare click se è su un'azione rapida
    }
    if (onClick) {
      onClick(notification);
    }
  };

  const handleMarkAsRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMarkAsRead(notification.id);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(notification.id);
  };

  const handleToggleSelect = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleSelect?.(notification.id);
  };

  return (
    <div
      className={`
        group relative flex items-start gap-3 px-4 py-3 cursor-pointer
        transition-all duration-200 ease-out
        ${!notification.read
          ? 'bg-blue-50/50 dark:bg-blue-950/20 hover:bg-blue-50 dark:hover:bg-blue-950/30'
          : 'hover:bg-accent/50'
        }
        ${isSelected ? 'bg-accent ring-1 ring-primary/20' : ''}
        ${compact ? 'py-2' : ''}
      `}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Checkbox per selezione multipla */}
      {showCheckbox && (
        <div
          className={`
            quick-action flex-shrink-0 mt-1 transition-opacity duration-200
            ${isHovered || isSelected ? 'opacity-100' : 'opacity-0'}
          `}
          onClick={handleToggleSelect}
        >
          <div className={`
            w-4 h-4 rounded border-2 flex items-center justify-center cursor-pointer
            transition-colors duration-150
            ${isSelected
              ? 'bg-primary border-primary'
              : 'border-muted-foreground/40 hover:border-primary/60'
            }
          `}>
            {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
          </div>
        </div>
      )}

      {/* Indicatore non letto - linea laterale */}
      {!notification.read && (
        <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-primary" />
      )}

      {/* Icona tipo notifica */}
      <div className={`
        flex-shrink-0 mt-0.5 w-8 h-8 rounded-lg ${config.bgColor}
        flex items-center justify-center transition-transform duration-200
        ${isHovered ? 'scale-105' : ''}
      `}>
        <IconComponent className={`h-4 w-4 ${config.color}`} />
      </div>

      {/* Contenuto principale */}
      <div className="flex-1 min-w-0">
        {/* Riga superiore: titolo + badge + tempo */}
        <div className="flex items-start justify-between gap-2 mb-0.5">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <h4 className={`
              text-sm truncate
              ${!notification.read ? 'font-semibold text-foreground' : 'font-medium text-foreground/90'}
            `}>
              {notification.title}
            </h4>

            {/* Badge categoria */}
            {!compact && (
              <span className={`
                hidden sm:inline-flex flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide
                ${categoryColor}
              `}>
                {categoryLabel}
              </span>
            )}
          </div>

          {/* Tempo + indicatore non letto */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {timeAgo}
            </span>
            {!notification.read && (
              <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
            )}
          </div>
        </div>

        {/* Messaggio */}
        <p className={`
          text-sm text-muted-foreground leading-relaxed
          ${compact ? 'line-clamp-1' : 'line-clamp-2'}
        `}>
          {notification.message}
        </p>

        {/* Dettagli (titolo, scadenza, Lead...) e descrizione, se la notifica li porta */}
        {!compact && <NotificationDetails metadata={notification.metadata} />}

        {/* Riga inferiore: sender + link */}
        {!compact && (
          <div className="flex items-center justify-between gap-2 mt-1.5">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {notification.sender_name && (
                <span className="font-medium">{notification.sender_name}</span>
              )}
            </div>

            {/* Quick Actions - visibili on hover */}
            <div className={`
              quick-action flex items-center gap-1 transition-all duration-200
              ${isHovered ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-2 pointer-events-none'}
            `}>
              {!notification.read && (
                <button
                  onClick={handleMarkAsRead}
                  className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                  title="Segna come letta"
                >
                  <CheckCircle className="h-3.5 w-3.5" />
                </button>
              )}

              {notification.link && (
                <Link
                  to={notification.link}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!notification.read) {
                      onMarkAsRead(notification.id);
                    }
                  }}
                  className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-primary transition-colors"
                  title="Apri"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </Link>
              )}

              <button
                onClick={handleDelete}
                className="p-1.5 rounded-md hover:bg-red-100 dark:hover:bg-red-900/30 text-muted-foreground hover:text-red-600 dark:hover:text-red-400 transition-colors"
                title="Elimina"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Componente per gruppo di notifiche con header data
interface NotificationGroupProps {
  title: string;
  notifications: (Notification & { event_type?: string; sender_name?: string; isWhatsApp?: boolean })[];
  onMarkAsRead: (id: string) => void;
  onDelete: (id: string) => void;
  onClick?: (notification: Notification) => void;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  showCheckbox?: boolean;
  defaultExpanded?: boolean;
}

export function NotificationGroup({
  title,
  notifications,
  onMarkAsRead,
  onDelete,
  onClick,
  selectedIds = new Set(),
  onToggleSelect,
  showCheckbox = false,
  defaultExpanded = true,
}: NotificationGroupProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="border-b border-border last:border-b-0">
      {/* Header gruppo */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-muted/30 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className={`
            text-xs font-semibold uppercase tracking-wider text-muted-foreground
            transition-transform duration-200
            ${isExpanded ? '' : 'transform'}
          `}>
            {title}
          </span>
          <span className="text-xs text-muted-foreground">
            ({notifications.length})
          </span>
          {unreadCount > 0 && (
            <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-primary text-primary-foreground rounded-full">
              {unreadCount}
            </span>
          )}
        </div>

        <svg
          className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Lista notifiche */}
      <div className={`
        overflow-hidden transition-all duration-300 ease-out
        ${isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}
      `}>
        {notifications.map((notification) => (
          <NotificationItem
            key={notification.id}
            notification={notification}
            onMarkAsRead={onMarkAsRead}
            onDelete={onDelete}
            onClick={onClick}
            isSelected={selectedIds.has(notification.id)}
            onToggleSelect={onToggleSelect}
            showCheckbox={showCheckbox}
          />
        ))}
      </div>
    </div>
  );
}

export { notificationTypeConfig, getCategoryFromEventType, categoryColors, categoryLabels };
